import React from 'react';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, Legend,
  CartesianGrid, ResponsiveContainer, ReferenceLine,
} from 'recharts';

// A chart drawn inside a Claude Code panel reply.
//
// ⚠️ WHY THIS EXISTS. Greg, 2026-10-12, after asking the panel to chart some numbers and
// getting a whole new report page for it: "I don't think every piece of data we're going
// to use more than sometimes. Some will be just one-offs that you're not going to want to
// build a report from." So a reply can carry its own chart and nothing gets added to CPx.
//
// The reply holds a fenced block, and the JSON inside it is the whole chart:
//
//   ```chart
//   { "type": "bar", "title": "Profit by order size", "x": "band",
//     "series": [{ "key": "profit", "label": "Profit" }], "format": "money",
//     "data": [{ "band": "$0 to $99", "profit": -412.5 }] }
//   ```
//
// type: bar | line | pie.   format: money | percent | number.
// bar also takes "stacked": true and "horizontal": true (long labels read better sideways).
// pie uses the first series only.
//
// ⚠️ THE SPEC IS DATA, NEVER MARKUP. The same transcript renders what a person typed, so
// nothing in here may turn a string into HTML. A spec that does not parse is shown as the
// text it was, which is ugly and honest, rather than as an empty box.

const COLORS = ['#00395c', '#dc582a', '#2a8c82', '#d9a441', '#6d8290', '#7b4b8a', '#4f86a8', '#a3552b'];
const LOSS = '#b3372e';

const fmt = (kind) => (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v == null ? '' : v);
  if (kind === 'money') {
    const cents = Math.abs(n) < 100 && n % 1 !== 0;
    return `${n < 0 ? '-' : ''}$${Math.abs(n).toLocaleString('en-US', {
      minimumFractionDigits: cents ? 2 : 0, maximumFractionDigits: cents ? 2 : 0,
    })}`;
  }
  if (kind === 'percent') return `${n.toLocaleString('en-US', { maximumFractionDigits: 1 })}%`;
  return n.toLocaleString('en-US', { maximumFractionDigits: 2 });
};

export function parseChartSpec(raw) {
  try {
    const s = JSON.parse(raw);
    if (!s || !Array.isArray(s.data) || !s.data.length) return null;
    const first = s.data[0] || {};
    const x = s.x || Object.keys(first).find((k) => typeof first[k] === 'string') || Object.keys(first)[0];
    let series = Array.isArray(s.series) ? s.series : [];
    series = series
      .map((v) => (typeof v === 'string' ? { key: v, label: v } : v))
      .filter((v) => v && v.key);
    // No series named: chart every numeric column that is not the label.
    if (!series.length) {
      series = Object.keys(first)
        .filter((k) => k !== x && Number.isFinite(Number(first[k])))
        .map((k) => ({ key: k, label: k }));
    }
    if (!series.length) return null;
    const data = s.data.slice(0, 200).map((row) => {
      const out = { ...row };
      for (const sr of series) out[sr.key] = Number(row[sr.key]) || 0;
      return out;
    });
    return {
      type: ['bar', 'line', 'pie'].includes(s.type) ? s.type : 'bar',
      title: s.title ? String(s.title) : '',
      note: s.note ? String(s.note) : '',
      x, series, data,
      format: s.format || 'number',
      stacked: !!s.stacked,
      horizontal: !!s.horizontal,
    };
  } catch (_) {
    return null;
  }
}

export default function ChatChart({ spec }) {
  const { type, title, note, x, series, data, format, stacked, horizontal } = spec;
  const f = fmt(format);
  const single = series.length === 1;
  const hasLoss = data.some((d) => series.some((s) => d[s.key] < 0));
  const tick = { fontSize: 11, fill: '#4a6675' };
  const height = horizontal ? Math.max(220, data.length * 30 + 60) : 300;

  let chart;
  if (type === 'pie') {
    const key = series[0].key;
    chart = (
      <PieChart>
        <Pie data={data.filter((d) => d[key] > 0)} dataKey={key} nameKey={x} outerRadius="78%"
          label={(p) => `${p.name}: ${f(p.value)}`} labelLine={false} isAnimationActive={false}>
          {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
        </Pie>
        <Tooltip formatter={(v) => f(v)} />
      </PieChart>
    );
  } else if (type === 'line') {
    chart = (
      <LineChart data={data} margin={{ top: 8, right: 16, bottom: 4, left: 4 }}>
        <CartesianGrid stroke="#e8eef2" vertical={false} />
        <XAxis dataKey={x} tick={tick} />
        <YAxis tick={tick} tickFormatter={f} width={64} />
        <Tooltip formatter={(v) => f(v)} />
        {!single && <Legend wrapperStyle={{ fontSize: 12 }} />}
        {hasLoss && <ReferenceLine y={0} stroke="#9fb0ba" />}
        {series.map((s, i) => (
          <Line key={s.key} type="monotone" dataKey={s.key} name={s.label || s.key}
            stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={{ r: 2.5 }} isAnimationActive={false} />
        ))}
      </LineChart>
    );
  } else {
    const bars = series.map((s, i) => (
      <Bar key={s.key} dataKey={s.key} name={s.label || s.key} fill={COLORS[i % COLORS.length]}
        stackId={stacked ? 'a' : undefined} isAnimationActive={false} maxBarSize={56}>
        {/* One series: a losing bar is red, so the bad band is the first thing seen. */}
        {single && data.map((d, n) => <Cell key={n} fill={d[s.key] < 0 ? LOSS : COLORS[0]} />)}
      </Bar>
    ));
    chart = horizontal ? (
      <BarChart data={data} layout="vertical" margin={{ top: 4, right: 20, bottom: 4, left: 4 }}>
        <CartesianGrid stroke="#e8eef2" horizontal={false} />
        <XAxis type="number" tick={tick} tickFormatter={f} />
        <YAxis type="category" dataKey={x} tick={tick} width={130} />
        <Tooltip formatter={(v) => f(v)} />
        {!single && <Legend wrapperStyle={{ fontSize: 12 }} />}
        {hasLoss && <ReferenceLine x={0} stroke="#9fb0ba" />}
        {bars}
      </BarChart>
    ) : (
      <BarChart data={data} margin={{ top: 8, right: 16, bottom: 4, left: 4 }}>
        <CartesianGrid stroke="#e8eef2" vertical={false} />
        <XAxis dataKey={x} tick={tick} interval={0} />
        <YAxis tick={tick} tickFormatter={f} width={64} />
        <Tooltip formatter={(v) => f(v)} />
        {!single && <Legend wrapperStyle={{ fontSize: 12 }} />}
        {hasLoss && <ReferenceLine y={0} stroke="#9fb0ba" />}
        {bars}
      </BarChart>
    );
  }

  return (
    <div className="cb-chart">
      {title && <div className="cb-chart-title">{title}</div>}
      <div style={{ width: '100%', height }}>
        <ResponsiveContainer width="100%" height="100%">{chart}</ResponsiveContainer>
      </div>
      {note && <div className="cb-chart-note">{note}</div>}
    </div>
  );
}
